import {
  dropChipsTouching,
  failChip,
  pendingChip,
  removeChip,
  resolveChips,
} from '../results/filter_chips_state.js';
import type { ExplorerEvent, ExplorerState } from './explorer_state.js';

type Handler<K extends ExplorerEvent['t']> = (
  state: ExplorerState,
  ev: Extract<ExplorerEvent, { t: K }>,
) => ExplorerState;

/**
 * Os eventos do filtro por texto (spec flight-text-filter-chips + r2), fora do `explorer_state.ts`
 * pelo teto de linhas dele. `chip_resolved` passa a perna ativa, o tamanho do roteiro e se a ativa
 * ainda está sem lista: chip de outra perna — ou da ativa sem lista (#3559) — fica `deferred`
 * (R2-D3). `filter_changed`/`filter_reset`/`sort_changed` vieram junto
 * (mesmo teto de linhas): mexem em `chips` (via `dropChipsTouching`) tanto quanto os `chip_*`.
 * `chip_resolved` também intercepta o chip de CABINE (R2-D4): ela não é corte — sai da lista antes
 * de `resolveChips` e vira `cabinRequest`, que `cabin_request_consumed` zera quando o componente
 * consome (`useCabinRequest`).
 */
export const CHIP_HANDLERS: {
  chip_pending: Handler<'chip_pending'>;
  chip_resolved: Handler<'chip_resolved'>;
  chip_failed: Handler<'chip_failed'>;
  chip_removed: Handler<'chip_removed'>;
  filter_changed: Handler<'filter_changed'>;
  filter_reset: Handler<'filter_reset'>;
  sort_changed: Handler<'sort_changed'>;
  cabin_request_consumed: Handler<'cabin_request_consumed'>;
} = {
  chip_pending: (state, ev) => ({ ...state, ...pendingChip(state, ev.id, ev.text) }),
  chip_resolved: (state, ev) => {
    // Sem o pendente (reset/nova busca já limpou `chips`), a resposta é stale — mesma guarda de
    // `resolveChips`, hoisted pra ANTES do desvio de cabine (senão ele grava `cabinRequest` mesmo
    // com o pedido já descartado, disparando busca de cabine depois que a pessoa resetou o estado).
    if (!state.chips.some((chip) => chip.id === ev.id)) return state;
    // Cabine NÃO é corte (R2-D4): sai da lista de chips e vira pedido pro seam da cabine.
    const cabin = ev.intent.chips.find((chip) => chip.status === 'applied' && chip.patch?.cabin)
      ?.patch?.cabin;
    const intent = cabin
      ? { ...ev.intent, chips: ev.intent.chips.filter((chip) => !chip.patch?.cabin) }
      : ev.intent;
    // "Sem lista" com a MESMA régua do `resetFilter` (`offers.length > 0` sobre o `offersOf` de
    // `explorer_state.ts`: só `ready`/`seeded` têm lista), e não pela fase: a `ready` vazia que
    // espera a página 1 do scroll também tem primeiro quadro (`refine_ok` firstFrame) pela frente.
    // Espelhado aqui em vez de importar `activeOffers`: o import de valor fecharia o ciclo com
    // `explorer_state.ts`, que já importa este módulo.
    const scene = state.legs[state.activeLeg];
    const activeEmpty =
      !(scene?.phase === 'ready' || scene?.phase === 'seeded') || scene.offers.length === 0;
    return {
      ...state,
      ...(cabin ? { cabinRequest: cabin } : {}),
      ...resolveChips(state, ev.id, intent, ev.ctx, {
        active: state.activeLeg,
        count: state.legs.length,
        activeEmpty,
      }),
    };
  },
  chip_failed: (state, ev) => ({ ...state, ...failChip(state, ev.id) }),
  chip_removed: (state, ev) => ({ ...state, ...removeChip(state, ev.id) }),
  filter_changed: (state, ev) => ({
    ...state,
    filter: ev.filter,
    chips: dropChipsTouching(state.chips, state.filter, ev.filter),
  }),
  filter_reset: (state, ev) => ({ ...state, filter: ev.filter, chips: [] }),
  sort_changed: (state, ev) => ({
    ...state,
    // Ordem local NA HORA (o clique responde) e o refine em seguida devolve a
    // página ordenada pelo provider, que volta a mandar.
    serverOrderedLeg: null,
    filter: { ...state.filter, sort: ev.sort },
    chips: dropChipsTouching(state.chips, state.filter, { ...state.filter, sort: ev.sort }),
  }),
  cabin_request_consumed: (state) => ({ ...state, cabinRequest: null }),
};
